// La superficie del encabezado de referidos — la tira de fotos sobre negro.
//
// Vivía dentro de `pages/Referidos.tsx` (ADR-2026-08-28-referidos-mas-visual). Se
// extrajo acá cuando el popup de anuncio de la feature pidió la MISMA cabecera
// (ADR-2026-08-31-anuncios-de-funcionalidad): dos copias de la tira se
// desincronizan al primer retoque, y son justamente las dos superficies que tienen
// que verse como la misma cosa — el anuncio presenta la pantalla a la que lleva.

/** Negro azulado del encabezado. Lo usan el fondo y todos sus degradés. */
export const NEGRO_REFERIDOS = '#0B0B12';

/**
 * El azul de marca como hex literal y no como token: `brand.primary` está definido
 * sin `<alpha-value>`, así que Tailwind no le puede aplicar modificadores de opacidad
 * (ver la nota larga en `pages/Referidos.tsx`).
 */
export const AZUL_REFERIDOS = 'var(--brand-primary)';

/**
 * Las fotos del encabezado. Son un set curado de generaciones de Atlas servido desde
 * `public/`, y no las de la propia cuenta: el encabezado tiene que verse igual de bien
 * en una cuenta recién creada que no generó nada.
 */
export const FOTOS_TIRA = [
  '/images/referidos/muestra-1.jpg',
  '/images/referidos/muestra-2.jpg',
  '/images/referidos/muestra-3.jpg',
  '/images/referidos/muestra-4.jpg',
  '/images/referidos/muestra-5.jpg',
  '/images/referidos/muestra-6.jpg',
];

export interface TiraDeFotosProps {
  /** Alto de la tira. Lo pone el consumidor: la pantalla y el popup no miden igual. */
  className?: string;
  /**
   * Cuántas columnas se ven abajo de `sm`. De 6 fotos, la pantalla muestra 3 en
   * teléfono: a 390px de ancho, seis miniaturas quedan en tiras de 60px donde no se
   * distingue ninguna. El popup puede mostrar más porque su tira es más corta y las
   * fotos se leen igual — ahí lo que importa es la textura, no cada foto.
   */
  columnasMobile?: number;
}

/**
 * La tira. `aria-hidden`: es textura, no contenido — las fotos no dicen nada que el
 * texto de al lado no diga.
 */
export function TiraDeFotos({
  className = 'h-[200px] sm:h-[290px]',
  columnasMobile = 3,
}: TiraDeFotosProps) {
  return (
    <div className={`relative w-full overflow-hidden ${className}`} aria-hidden="true">
      <div className="flex h-full w-full gap-2">
        {FOTOS_TIRA.map((foto, i) => (
          <div
            key={foto}
            /* Todas al ras y separadas por un canal de 8px, sin desfase vertical.
               Se probó corriéndolas una sí y una no para despegarlas: el desfase
               deja un rectángulo negro arriba de cada columna corrida y eso no se
               lee como diseño, se lee como fotos que no cargaron. El canal separa
               igual y no rompe el borde superior. */
            className={`h-full flex-1 overflow-hidden bg-white/[0.04] ${
              i >= columnasMobile ? 'hidden sm:block' : ''
            }`}
          >
            <img
              src={foto}
              alt=""
              /* Sin `loading="lazy"`: están arriba de todo, siempre a la vista al
                 entrar. Diferirlas sólo consigue que el encabezado aparezca vacío
                 el primer instante. */
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>
      {/* Funde la tira con el fondo. Sin esto, las fotos cortan con una línea
          recta contra el negro y parecen pegadas encima, no parte del bloque. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, ${NEGRO_REFERIDOS}40 0%, ${NEGRO_REFERIDOS}00 20%, ${NEGRO_REFERIDOS}d9 66%, ${NEGRO_REFERIDOS} 86%)`,
        }}
      />
    </div>
  );
}

/**
 * El azul de Atlas como LUZ QUE BAJA DE LAS FOTOS, no como panel ni como degradé de
 * fondo. Va ancho y bajo, pegado a la tira y no en el centro: centrado y opaco
 * quedaba una mancha azul —el recurso genérico de cualquier landing—; así se lee como
 * el derrame de luz de las imágenes de arriba, que es lo que pasa en un estudio de
 * fotos.
 *
 * El `top` lo pone el consumidor porque depende de dónde termina su tira.
 */
export function LuzAzul({ className }: { className: string }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute left-1/2 -translate-x-1/2 rounded-[50%] blur-[90px] ${className}`}
      style={{ background: AZUL_REFERIDOS }}
    />
  );
}

export default TiraDeFotos;
